import React, { useEffect, useState } from 'react';
import { useLocation, useParams } from "react-router";
import { useIntl } from "react-intl";
import classNames from "classnames";
import autoCompleteKeywordRepository from "@/repository/autoCompleteKeywordRepository";
import { useAutocomplete } from '@/query/autocomplete';
import { usePaginatedAutocompleteKeywords } from "@/query/autocompleteKeyword";
import { useAgentId, useModal, usePageParam, useRouteHandler } from "@/hook";
import Button from "@/component/Button";
import Searchbar from "@/component/input/Searchbar";
import UploadModal from "@/component/modal/UploadModal";
import PaginationBar from "@/component/PaginationBar";
import { BackIcon, DownloadIcon, LightningBoltIcon, UploadIcon } from "@/assets/svgs/icons";

import AutocompleteKeywordTable from "./component/AutocompleteKeywordTable";
import AutocompleteTestPanel from './component/AutocompleteTestPanel';


const AUTOCOMPLETE_UPLOAD_MODAL_ID = "autocomplete-upload-modal-id";
const AUTOCOMPLETE_KEYWORD_PAGE_SIZE = 15;

export interface AutocompleteKeywordPageParam {
    page: number;
    size: number;
    searchKeyword?: string;
    sortOrder: "asc" | "desc";
}

const AutocompleteKeywordView: React.FC = () => {
    const intl = useIntl();
    const agentId = useAgentId();
    const modal = useModal();
    const { autocompleteId } = useParams<{ autocompleteId: string }>();
    const location = useLocation();
    const { moveToAutocompleteKeyword, moveToAutocompleteList } = useRouteHandler();

    const [testPanelOpen, setTestPanelOpen] = useState(false);
    const { param, set: setParam } = usePageParam<AutocompleteKeywordPageParam>({
        page: 0,
        size: AUTOCOMPLETE_KEYWORD_PAGE_SIZE,
        sortOrder: "asc",
    });

    const { data: autocomplete } = useAutocomplete(agentId, autocompleteId!);
    const { data: autocompleteKeywordPage } = usePaginatedAutocompleteKeywords(
        agentId,
        autocompleteId!,
        param?.page,
        param?.size,
        param?.sortOrder,
        param?.searchKeyword
    );

    const onClickUpload = async (file: File, isCleanImport: boolean) => {
        return await autoCompleteKeywordRepository.import(agentId, autocompleteId!, file, isCleanImport);
    };

    useEffect(() => {
        if (location.state?.searchQuery) {
            setParam({
                searchKeyword: location.state.searchQuery,
            });
            moveToAutocompleteKeyword(agentId, autocompleteId!, { replace: true, state: null });
        }
        // eslint-disable-next-line
    }, [location.state?.searchQuery]);

    return (<>
        <UploadModal id={AUTOCOMPLETE_UPLOAD_MODAL_ID}
                     label={intl.formatMessage({ id: "i08023" })}
                     cleanImportText={intl.formatMessage({ id: "i08032" })}
                     sampleFileName="nlu_autocomplete_sample"
                     sampleFileColumns={[
                         { attribute: "text", description: intl.formatMessage({ id: "i08022" }) }
                     ]}
                     upload={onClickUpload}
        />

        <div className={classNames(
                 "agent-content-layout flex flex-col gap-1",
                 testPanelOpen && "test-panel-open"
             )}
        >
            <div className="search-bar-container w-md-page">
                <Searchbar lg width={680}
                           defaultValue={param?.searchKeyword}
                           placeholder={intl.formatMessage({ id: "i00070" })}
                           onSearch={value => setParam({ searchKeyword: value })}
                />
            </div>
            
            <div className="subtitle-container w-md-page !pr-0">
                <div className="subtitle-container-title">
                    {autocomplete?.name}
                </div>

                <div className="subtitle-container-btn-group">
                    <Button variant="text"
                            onClick={() => moveToAutocompleteList(agentId)}
                    >
                        <BackIcon className="size-3.5 mb-0.5" />
                        {intl.formatMessage({ id: "i08031" })}
                    </Button>
                    <Button variant="text"
                            onClick={() => modal.open(AUTOCOMPLETE_UPLOAD_MODAL_ID)}
                    >
                        <UploadIcon className="size-3.5"/>
                        {intl.formatMessage({ id: "b0004" })}
                    </Button>
                    <Button variant="text"
                            onClick={() => autoCompleteKeywordRepository.export(agentId, autocompleteId!)}
                    >
                        <DownloadIcon className="size-3.5"/>
                        {intl.formatMessage({ id: "b0005" })}
                    </Button>
                    <Button variant="text"
                            onClick={() => setTestPanelOpen(prev => !prev)}
                    >
                        <LightningBoltIcon className="size-3" />
                        {intl.formatMessage({ id: 'i08029' })}
                    </Button>
                </div>
            </div>

            <div className="table-test-container">
                <div className="table-pagination-container w-full max-w-md-page">
                    <AutocompleteKeywordTable page={autocompleteKeywordPage}
                                              pageParam={param}
                                              onClickSortHead={order => setParam({ sortOrder: order })}
                    />
                    <PaginationBar onClickPage={page => setParam({ page })}
                                   pageData={autocompleteKeywordPage}
                    />
                </div>
                
                <AutocompleteTestPanel open={testPanelOpen}
                                       onClose={() => setTestPanelOpen(false)}
                />
            </div>
        </div>
    </>);
};


export default AutocompleteKeywordView;
